// client/src/pages/secure/PropertyFlyerPage.js — a print flyer for one of our own listings
// (/flyer/:id, outside the dashboard frame so only the flyer prints). The brokerage
// identity is this state's (lib/listingSources/display) and the advertising checklist
// beside it on screen is this state's (lib/advertisingRules) — the flyer is advertising,
// so it goes through the same review as any other ad.
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { addressLine, placeLine, onImgError } from '../../utils/properties';
import { VirtualStagingBadge } from '../../components/shared/VirtualStagingBadge';

const PropertyFlyerPage = () => {
    const { id } = useParams();
    const api = useApiClient();
    const [d, setD] = useState(null);
    const [error, setError] = useState('');
    useEffect(() => { api.get(`/api/property-search/flyer/${id}`).then(setD).catch((e) => setError(e.message)); }, [api, id]);
    if (error) return <div className="p-8"><p className="text-red-700">{error}</p><Link className="underline" to={`/dashboard/properties/${id}`}>Back</Link></div>;
    if (!d) return <p className="p-8">Loading…</p>;
    const l = d.listing;
    const photos = (l.photos || []).slice(0, 4);
    const facts = [
        ['Price', l.price_label], ['Type', l.type_detail || l.type_label], ['Bedrooms', l.beds], ['Bathrooms', l.baths],
        ['Size', l.sqft ? `${Number(l.sqft).toLocaleString('en-US')} sq ft` : null], ['Lot', l.lot_acres ? `${l.lot_acres} acres` : null],
        ['Year built', l.year_built], ['Zoning', l.zoning], ['Cap rate', l.cap_rate ? `${l.cap_rate}%` : null], ['Lease', l.lease_terms], ['MLS #', l.mls_number],
    ].filter(([, v]) => v !== null && v !== undefined && v !== '');
    return (
        <div className="min-h-screen bg-slate-100 print:bg-white">
            <div className="mx-auto max-w-5xl p-4 print:hidden flex flex-wrap gap-3 items-center">
                <Link className="text-indigo-700 underline" to={`/dashboard/properties/${id}`}>← Back to the property</Link>
                <button type="button" onClick={() => window.print()} className="rounded-md bg-slate-900 px-4 py-2 font-semibold text-white">Print</button>
                {d.public_url
                    ? <a className="text-indigo-700 underline" href={d.public_url} target="_blank" rel="noreferrer">Open the public page</a>
                    : <span className="text-sm text-amber-800">Not on the website: the seller withheld it from internet display (print is still allowed).</span>}
            </div>
            <div className="mx-auto max-w-5xl grid gap-4 lg:grid-cols-3 px-4 pb-8 print:block print:p-0">
                <article className="bg-white p-8 shadow lg:col-span-2 print:shadow-none print:p-0">
                    <p className="text-sm font-semibold uppercase tracking-wide text-indigo-800">{l.status_label}</p>
                    <h1 className="text-3xl font-bold text-slate-900">{addressLine(l)}</h1>
                    <p className="text-lg text-slate-700">{placeLine(l)}</p>
                    {photos[0] && (
                        <div className="relative mt-4">
                            <img src={photos[0].url} onError={onImgError} alt={photos[0].caption || addressLine(l)} className="w-full max-h-96 object-cover" />
                            {photos[0].virtually_staged && <VirtualStagingBadge />}
                        </div>
                    )}
                    {photos.length > 1 && (
                        <div className="mt-2 grid grid-cols-3 gap-2">
                            {photos.slice(1).map((p, i) => (
                                <div key={i} className="relative">
                                    <img src={p.url} onError={onImgError} alt={p.caption || ''} className="h-28 w-full object-cover" />
                                    {p.virtually_staged && <VirtualStagingBadge className="text-[9px] px-1" />}
                                </div>
                            ))}
                        </div>
                    )}
                    <dl className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
                        {facts.map(([k, v]) => <div key={k}><dt className="text-xs text-slate-600">{k}</dt><dd className="font-semibold text-slate-900">{v}</dd></div>)}
                    </dl>
                    {l.description && <p className="mt-4 whitespace-pre-line text-slate-800">{l.description}</p>}
                    <footer className="mt-6 border-t pt-4 text-sm text-slate-800">
                        {d.agent && <p><strong>{d.agent.name}</strong>{d.agent.license_number ? ` — ${d.agent.licence_label}${d.agent.license_number}` : ''}{d.agent.phone ? ` · ${d.agent.phone}` : ''}{d.agent.email ? ` · ${d.agent.email}` : ''}</p>}
                        <p className="text-base font-semibold">{d.identity.brokerage_name}, {d.identity.broker_label}</p>
                        <p>{[d.identity.location, d.identity.phone, d.identity.url].filter(Boolean).join(' · ')}</p>
                        {d.public_url && <p className="mt-1 break-all">{d.public_url}</p>}
                        <p className="mt-2 text-xs text-slate-600">Information deemed reliable but not guaranteed. Equal Housing Opportunity.</p>
                    </footer>
                </article>
                <aside className="bg-white p-4 shadow text-sm print:hidden">
                    <h2 className="font-semibold text-slate-900">Before this goes out ({d.state})</h2>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-slate-700">
                        {(d.checklist || []).map((c) => <li key={c.key}>{c.text} <span className="text-xs text-slate-500">({c.cite})</span></li>)}
                    </ul>
                    {(d.reminders || []).length > 0 && <ul className="mt-2 space-y-1 text-xs text-slate-600">{d.reminders.map((r, i) => <li key={i}>{r}</li>)}</ul>}
                    <p className="mt-3 text-xs text-slate-600">{d.identity.rule} ({d.identity.cite})</p>
                </aside>
            </div>
        </div>
    );
};

export default PropertyFlyerPage;
